import type { FoodItem, FoodItemServingSize as ServingSize, Nutrition } from "@macro-tracker/models";
import { getDbForCurrentUser } from "@/services/rxdb/database";
import { USER_FOODS_COLLECTION, type UserFoodDocType } from "@macro-tracker/models";
import { generateId } from "@/utils/id";

export type SyncableFoodItem = FoodItem;

// ---------------------------------------------------------------------------
// RxDB ↔ SyncableFoodItem mapping
// ---------------------------------------------------------------------------

/** Convert a flat RxDB userFood doc to the rich frontend FoodItem shape. */
const docToFoodItem = (doc: UserFoodDocType): SyncableFoodItem =>
  ({
    id: doc.id,
    name: doc.name,
    brand: doc.brand ?? undefined,
    servingSizes: [
      {
        id: 'default',
        label: doc.servingUnit,
        grams: doc.servingSize ?? null,
        nutrition: {
          calories: doc.calories,
          protein: doc.protein,
          carbs: doc.carbs,
          fat: doc.fat,
        },
        isWeightBased: false,
      },
    ],
    isFavorite: doc.isFavorite,
    isCustom: true,
    isProvisional: doc.isProvisional,
    isArchived: doc.isArchived,
    isSharedByOwner: doc.isSharedByOwner,
    createdAt: doc.createdAt,
    upc: doc.upc ?? undefined,
  }) as SyncableFoodItem;

/** Convert a frontend FoodItem to the flat RxDB userFood doc shape. */
const foodItemToDoc = (
  foodItem: SyncableFoodItem,
  userId: string,
): Omit<UserFoodDocType, '_deleted'> => {
  const serving = foodItem.servingSizes?.[0];
  return {
    id: foodItem.id,
    userId,
    name: foodItem.name,
    brand: foodItem.brand ?? null,
    upc: (foodItem as any).upc ?? null,
    calories: serving?.nutrition?.calories ?? 0,
    protein: serving?.nutrition?.protein ?? 0,
    carbs: serving?.nutrition?.carbs ?? 0,
    fat: serving?.nutrition?.fat ?? 0,
    servingSize: serving?.grams ?? null,
    servingUnit: serving?.label ?? 'serving',
    isFavorite: foodItem.isFavorite ?? false,
    isArchived: foodItem.isArchived ?? false,
    isProvisional: (foodItem as any).isProvisional ?? false,
    isSharedByOwner: (foodItem as any).isSharedByOwner ?? false,
    createdAt: foodItem.createdAt ?? new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

const getCurrentUserId = async (): Promise<string> => {
  const { useAuthSessionStore } = await import('@/stores/authSessionStore');
  return useAuthSessionStore().user?.id ?? '';
};

export const saveFoodItem = async (foodItem: SyncableFoodItem): Promise<SyncableFoodItem> => {
  const db = await getDbForCurrentUser();
  const userId = await getCurrentUserId();
  const id = foodItem.id || generateId();
  const doc = foodItemToDoc({ ...foodItem, id }, userId);
  await db[USER_FOODS_COLLECTION].upsert(doc as any);
  const saved = await db[USER_FOODS_COLLECTION].findOne(id).exec();
  return saved ? docToFoodItem(saved.toMutableJSON()) : { ...foodItem, id };
};

export const getFoodItemById = async (id: string): Promise<SyncableFoodItem | null> => {
  const db = await getDbForCurrentUser();
  const doc = await db[USER_FOODS_COLLECTION].findOne(id).exec();
  return doc ? docToFoodItem(doc.toMutableJSON()) : null;
};

export const getFoodItems = async (): Promise<SyncableFoodItem[]> => {
  const db = await getDbForCurrentUser();
  const docs = await db[USER_FOODS_COLLECTION].find({ selector: { isArchived: false } }).exec();
  return docs.map((d) => docToFoodItem(d.toMutableJSON()));
};

export const getAllFoodItems = async (): Promise<SyncableFoodItem[]> => {
  const db = await getDbForCurrentUser();
  const docs = await db[USER_FOODS_COLLECTION].find().exec();
  return docs.map((d) => docToFoodItem(d.toMutableJSON()));
};

export const updateFoodItem = async (
  id: string,
  updates: Partial<SyncableFoodItem>,
): Promise<SyncableFoodItem | null> => {
  const db = await getDbForCurrentUser();
  const doc = await db[USER_FOODS_COLLECTION].findOne(id).exec();
  if (!doc) return null;
  const current = docToFoodItem(doc.toMutableJSON());
  const merged = { ...current, ...updates };
  const userId = await getCurrentUserId();
  const newDoc = foodItemToDoc(merged, userId);
  await doc.incrementalModify((d) => {
    Object.assign(d, newDoc);
    d.updatedAt = new Date().toISOString();
    return d;
  });
  const updated = await db[USER_FOODS_COLLECTION].findOne(id).exec();
  return updated ? docToFoodItem(updated.toMutableJSON()) : null;
};

export const removeFoodItem = async (id: string): Promise<boolean> => {
  const db = await getDbForCurrentUser();
  const doc = await db[USER_FOODS_COLLECTION].findOne(id).exec();
  if (!doc) return false;
  await doc.incrementalModify((d) => {
    d.isArchived = true;
    d.updatedAt = new Date().toISOString();
    return d;
  });
  return true;
};

export const clearFoodItems = async (): Promise<void> => {
  const db = await getDbForCurrentUser();
  const docs = await db[USER_FOODS_COLLECTION].find().exec();
  await Promise.all(docs.map((d) => d.remove()));
};

// Legacy sync stubs — no-ops now that RxDB handles sync.
export const getFoodItemsForSync = async (): Promise<SyncableFoodItem[]> => [];
export const markFoodItemForSync = async (
  _id: string, _operation: 'create' | 'update' | 'delete',
): Promise<void> => { /* handled by RxDB replication */ };
export const clearFoodItemSyncStatus = async (_id: string): Promise<void> => { /* no-op */ };

export const getPer100gNutrition = (
  servingSize: ServingSize,
): Nutrition | null => {
  if (!servingSize.grams || servingSize.grams <= 0) {
    return null;
  }

  const scale = 100 / servingSize.grams;
  const sourceLabelNutrients = servingSize.nutrition.labelNutrients;
  return {
    calories: servingSize.nutrition.calories * scale,
    protein: servingSize.nutrition.protein * scale,
    carbs: servingSize.nutrition.carbs * scale,
    fat: servingSize.nutrition.fat * scale,
    labelNutrients: sourceLabelNutrients
      ? {
          saturatedFatG: sourceLabelNutrients.saturatedFatG === undefined ? undefined : sourceLabelNutrients.saturatedFatG * scale,
          transFatG: sourceLabelNutrients.transFatG === undefined ? undefined : sourceLabelNutrients.transFatG * scale,
          cholesterolMg: sourceLabelNutrients.cholesterolMg === undefined ? undefined : sourceLabelNutrients.cholesterolMg * scale,
          sodiumMg: sourceLabelNutrients.sodiumMg === undefined ? undefined : sourceLabelNutrients.sodiumMg * scale,
          dietaryFiberG: sourceLabelNutrients.dietaryFiberG === undefined ? undefined : sourceLabelNutrients.dietaryFiberG * scale,
          totalSugarsG: sourceLabelNutrients.totalSugarsG === undefined ? undefined : sourceLabelNutrients.totalSugarsG * scale,
          addedSugarsG: sourceLabelNutrients.addedSugarsG === undefined ? undefined : sourceLabelNutrients.addedSugarsG * scale,
          vitaminDMcg: sourceLabelNutrients.vitaminDMcg === undefined ? undefined : sourceLabelNutrients.vitaminDMcg * scale,
          calciumMg: sourceLabelNutrients.calciumMg === undefined ? undefined : sourceLabelNutrients.calciumMg * scale,
          ironMg: sourceLabelNutrients.ironMg === undefined ? undefined : sourceLabelNutrients.ironMg * scale,
          potassiumMg: sourceLabelNutrients.potassiumMg === undefined ? undefined : sourceLabelNutrients.potassiumMg * scale,
        }
      : undefined,
  };
};
